import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { instructorVoice, type InstructorVoicePlayResult } from "@/lib/audio/instructorVoiceController";
import { preloadInstructorAvatars } from "@/components/launch/InstructorVoiceCaption";

const ENABLED_KEY = "heirwise.instructorVoice.enabled";
const VOLUME_KEY = "heirwise.instructorVoice.volume";

interface InstructorVoiceContextValue {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  volume: number;
  setVolume: (v: number) => void;
  playMoment: (momentId: string) => Promise<InstructorVoicePlayResult>;
  preload: (momentIds: string[]) => void;
  stop: () => void;
  speakingMomentId: string | null;
}

const InstructorVoiceContext = createContext<InstructorVoiceContextValue>({
  enabled: true,
  setEnabled: () => {},
  volume: 0.9,
  setVolume: () => {},
  playMoment: async (momentId: string) => ({ momentId, status: "unavailable" }),
  preload: () => {},
  stop: () => {},
  speakingMomentId: null,
});

function readStoredBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch { /* ignore */ }
  return fallback;
}
function readStoredNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, Math.min(1, n));
  } catch { /* ignore */ }
  return fallback;
}

export function InstructorVoiceProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(() => readStoredBool(ENABLED_KEY, true));
  // Default instructor voice volume — softened in stages from the
  // original 0.9 so mentor lines sit clearly under music/SFX.
  const [volume, setVolumeState] = useState<number>(() => readStoredNumber(VOLUME_KEY, 0.43));
  const [speakingMomentId, setSpeakingMomentId] = useState<string | null>(null);

  useEffect(() => { instructorVoice.setEnabled(enabled); }, [enabled]);
  useEffect(() => { instructorVoice.setVolume(volume); }, [volume]);

  // Warm instructor avatar bitmaps as soon as the audio provider mounts,
  // long before any caption tries to render.
  useEffect(() => { preloadInstructorAvatars(); }, []);

  useEffect(() => {
    setSpeakingMomentId(instructorVoice.getSpeakingMomentId());
    return instructorVoice.subscribe(() => {
      setSpeakingMomentId(instructorVoice.getSpeakingMomentId());
    });
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { localStorage.setItem(ENABLED_KEY, String(v)); } catch { /* ignore */ }
  }, []);
  const setVolume = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v));
    setVolumeState(c);
    try { localStorage.setItem(VOLUME_KEY, String(c)); } catch { /* ignore */ }
  }, []);

  const playMoment = useCallback((momentId: string) => {
    return instructorVoice.play(momentId);
  }, []);
  const preload = useCallback((momentIds: string[]) => {
    void instructorVoice.preload(momentIds);
  }, []);
  const stop = useCallback(() => { instructorVoice.stop(); }, []);

  const value = useMemo<InstructorVoiceContextValue>(
    () => ({ enabled, setEnabled, volume, setVolume, playMoment, preload, stop, speakingMomentId }),
    [enabled, setEnabled, volume, setVolume, playMoment, preload, stop, speakingMomentId]
  );

  return <InstructorVoiceContext.Provider value={value}>{children}</InstructorVoiceContext.Provider>;
}

export function useInstructorVoiceContext() { return useContext(InstructorVoiceContext); }